/**
 * Questionnaire API - forms, assessments, and patient submissions.
 */
import apiClient, { apiGet, apiGetList, apiPost, apiDelete, apiPut } from "./client";
import type {
  BulkQuestionnaireAssignmentResult,
  BulkSendQuestionnaireAssignmentsRequest,
  QuestionnaireAssignment,
  QuestionnaireAssignmentFilters,
  QuestionnaireAssignmentSummary,
  ProviderIntakeItem,
  ProviderIntakeSummary,
  QuestionnaireChartReview,
  QuestionnaireChartReviewDecisionRequest,
  QuestionnaireAutomation,
  QuestionnaireAutomationInput,
  QuestionnaireSettings,
  QuestionnaireTemplate,
  QuestionnaireTemplateInput,
  QuestionnaireSend,
  QuestionnaireResponseRecord,
  ResendQuestionnaireRequest,
  SendQuestionnaireAssignmentsRequest,
  SendFormRequest,
  PaginatedResponse,
} from "@/types";
import {
  adaptAssignment,
  adaptAssignmentSummary,
  adaptBulkAssignmentResult,
} from "./questionnaire.adapters";

export class QuestionnaireCapabilityError extends Error {
  constructor(capability: string) {
    super(`${capability} is not supported by the current questionnaire API`);
    this.name = "QuestionnaireCapabilityError";
  }
}

/** List available questionnaire templates (system + org-specific). */
export function listQuestionnaires(params?: {
  page?: number;
  per_page?: number;
  category?: string;
  search?: string;
  status?: string;
}): Promise<PaginatedResponse<QuestionnaireTemplate>> {
  return apiGetList<QuestionnaireTemplate>("/questionnaires", { params });
}

export function createQuestionnaire(
  input: QuestionnaireTemplateInput,
): Promise<QuestionnaireTemplate> {
  return apiPost<QuestionnaireTemplate>("/questionnaires", {
    ...input,
    status: "draft",
    version: "1.0",
    subject_type: "patient",
  });
}

export function updateQuestionnaire(
  id: string,
  input: Partial<QuestionnaireTemplateInput>,
): Promise<QuestionnaireTemplate> {
  return apiPut<QuestionnaireTemplate>(`/questionnaires/${id}`, input);
}

export function deleteQuestionnaire(id: string): Promise<void> {
  return apiDelete<void>(`/questionnaires/${id}`);
}

export function copyQuestionnaire(id: string): Promise<QuestionnaireTemplate> {
  return apiPost<QuestionnaireTemplate>(`/questionnaires/${id}/copy`, {});
}

export function createQuestionnaireVersion(id: string): Promise<QuestionnaireTemplate> {
  return apiPost<QuestionnaireTemplate>(`/questionnaires/${id}/versions`, {});
}

export function listQuestionnaireVersions(id: string): Promise<QuestionnaireTemplate[]> {
  return apiGet<QuestionnaireTemplate[]>(`/questionnaires/${id}/versions`);
}

export function publishQuestionnaire(id: string): Promise<QuestionnaireTemplate> {
  return apiPost<QuestionnaireTemplate>(`/questionnaires/${id}/publish`, {});
}

export function retireQuestionnaire(id: string): Promise<QuestionnaireTemplate> {
  return apiPost<QuestionnaireTemplate>(`/questionnaires/${id}/retire`, {});
}

export function listQuestionnaireAssignments(
  params?: QuestionnaireAssignmentFilters,
): Promise<PaginatedResponse<QuestionnaireAssignment>> {
  const requestedStatus = params?.status;
  const normalized = {
    ...params,
    status:
      requestedStatus === "all" || requestedStatus === "awaiting"
        ? undefined
        : requestedStatus === "action_needed"
          ? "action_required"
          : requestedStatus === "completed"
            ? "reviewed"
            : requestedStatus,
    channel: params?.channel === "all" ? undefined : params?.channel,
  };
  return apiGetList<QuestionnaireAssignment>("/questionnaire-assignments", { params: normalized })
    .then((page) => ({ ...page, data: page.data.map(adaptAssignment) }));
}

export function getQuestionnaireAssignment(id: string): Promise<QuestionnaireAssignment> {
  return apiGet<QuestionnaireAssignment>(`/questionnaire-assignments/${id}`)
    .then(adaptAssignment);
}

export function getQuestionnaireAssignmentSummary(
  params?: Omit<QuestionnaireAssignmentFilters, "page" | "per_page">,
): Promise<QuestionnaireAssignmentSummary> {
  return apiGet<QuestionnaireAssignmentSummary | { counts: Record<string, number> }>(
    "/questionnaire-assignments/summary",
    { params },
  ).then(adaptAssignmentSummary);
}

export function getProviderIntakeSummary(providerId?: string): Promise<ProviderIntakeSummary> {
  return apiGet<ProviderIntakeSummary>("/questionnaire-assignments/provider-summary", {
    params: providerId ? { provider_id: providerId } : undefined,
  });
}

export function listProviderIntake(params?: {
  provider_id?: string;
  page?: number;
  per_page?: number;
}): Promise<PaginatedResponse<ProviderIntakeItem>> {
  return apiGetList<ProviderIntakeItem>("/questionnaire-assignments/provider", { params });
}

export function sendQuestionnaireAssignments(
  input: SendQuestionnaireAssignmentsRequest,
): Promise<BulkQuestionnaireAssignmentResult> {
  const assignments = input.questionnaire_ids.map((questionnaireId) =>
    toBackendAssignment(input, questionnaireId),
  );
  if (assignments.length === 1) {
    return apiPost<QuestionnaireAssignment>("/questionnaire-assignments", assignments[0])
      .then((created) => ({ created: [adaptAssignment(created)], errors: [] }));
  }
  return apiPost<
    BulkQuestionnaireAssignmentResult | QuestionnaireAssignment[]
  >("/questionnaire-assignments/bulk", { assignments })
    .then(adaptBulkAssignmentResult);
}

export function bulkSendQuestionnaireAssignments(
  input: BulkSendQuestionnaireAssignmentsRequest,
): Promise<BulkQuestionnaireAssignmentResult> {
  const assignments = input.assignments.flatMap((assignment) =>
    assignment.questionnaire_ids.map((questionnaireId) =>
      toBackendAssignment(assignment, questionnaireId),
    ),
  );
  return apiPost<
    BulkQuestionnaireAssignmentResult | QuestionnaireAssignment[]
  >("/questionnaire-assignments/bulk", { assignments })
    .then(adaptBulkAssignmentResult);
}

function toBackendAssignment(
  input: SendQuestionnaireAssignmentsRequest,
  questionnaireId: string,
) {
  return {
    questionnaire_id: questionnaireId,
    patient_id: input.patient_id,
    channel: input.channel,
    channels: input.channels,
    recipient_email: input.recipient_email,
    recipient_phone: input.recipient_phone,
    due_at: input.due_at,
    reminder_schedule: input.reminder_schedule,
    notes: input.notes,
    ...(input.provider_id ? { provider_id: input.provider_id } : {}),
  };
}

export function cancelQuestionnaireAssignment(id: string): Promise<void> {
  return apiDelete<void>(`/questionnaire-assignments/${id}`);
}

export function resendQuestionnaireAssignment(
  id: string,
  payload?: ResendQuestionnaireRequest,
): Promise<QuestionnaireAssignment> {
  return apiPost<QuestionnaireAssignment>(`/questionnaire-assignments/${id}/resend`, payload ?? {}).then(
    adaptAssignment,
  );
}

export function remindQuestionnaireAssignment(id: string): Promise<QuestionnaireAssignment> {
  return apiPost<QuestionnaireAssignment>(`/questionnaire-assignments/${id}/remind`, {});
}

export function reviewQuestionnaireAssignment(
  id: string,
  _notes?: string,
): Promise<void> {
  return apiPost<void>(`/questionnaire-assignments/${id}/review`, {});
}

export function getQuestionnaireChartReview(id: string): Promise<QuestionnaireChartReview> {
  return apiGet<QuestionnaireChartReview>(`/questionnaire-assignments/${id}/chart-review`);
}

export function decideQuestionnaireChartReview(
  id: string,
  input: QuestionnaireChartReviewDecisionRequest,
): Promise<QuestionnaireChartReview> {
  return apiPost<QuestionnaireChartReview>(
    `/questionnaire-assignments/${id}/chart-review/decisions`,
    input,
  );
}

export function applyQuestionnaireChartReview(id: string): Promise<QuestionnaireChartReview> {
  return apiPost<QuestionnaireChartReview>(
    `/questionnaire-assignments/${id}/chart-review/apply`,
    {},
  );
}

export function listQuestionnaireAutomations(): Promise<QuestionnaireAutomation[]> {
  return apiGet<QuestionnaireAutomation[]>("/questionnaire-automations");
}

export function createQuestionnaireAutomation(
  input: QuestionnaireAutomationInput,
): Promise<QuestionnaireAutomation> {
  return apiPost<QuestionnaireAutomation>("/questionnaire-automations", input);
}

export function updateQuestionnaireAutomation(
  id: string,
  input: Partial<QuestionnaireAutomationInput>,
): Promise<QuestionnaireAutomation> {
  return listQuestionnaireAutomations().then((automations) => {
    const current = automations.find((automation) => automation.id === id);
    if (!current) throw new Error("Form automation not found");
    return apiPut<QuestionnaireAutomation>(`/questionnaire-automations/${id}`, {
      name: current.name,
      trigger: current.trigger,
      questionnaire_ids: current.questionnaire_ids,
      channel: current.channel,
      offset_hours: current.offset_hours,
      due_in_hours: current.due_in_hours,
      reminder_schedule: current.reminder_schedule ?? [],
      active: current.active,
      ...input,
    });
  });
}

export function deleteQuestionnaireAutomation(id: string): Promise<void> {
  return apiDelete<void>(`/questionnaire-automations/${id}`);
}

export function getQuestionnaireSettings(): Promise<QuestionnaireSettings> {
  return apiGet<QuestionnaireSettings>("/questionnaire-settings");
}

export function updateQuestionnaireSettings(
  input: QuestionnaireSettings,
): Promise<QuestionnaireSettings> {
  return apiPut<QuestionnaireSettings>("/questionnaire-settings", input);
}

/** Get a single questionnaire template by ID. */
export function getQuestionnaire(id: string): Promise<QuestionnaireTemplate> {
  return apiGet<QuestionnaireTemplate>(`/questionnaires/${id}`);
}

/**
 * Send a questionnaire form to a patient via email or SMS.
 * The patient receives a link they can fill out without logging in.
 */
export function sendQuestionnaire(
  req: SendFormRequest,
): Promise<QuestionnaireSend> {
  return sendQuestionnaireAssignments({
    patient_id: req.patient_id,
    questionnaire_ids: [req.questionnaire_id],
    channel: req.channel,
    recipient_email: req.recipient_email,
    recipient_phone: req.recipient_phone,
    due_at: req.due_at,
    reminder_schedule: req.reminder_schedule,
    notes: req.notes,
    provider_id: req.provider_id,
  }).then((result) => {
    const assignment = result.created[0];
    if (!assignment) throw new Error("The form couldn't be sent. Please try again.");
    if (result.errors.length > 0) {
      throw new Error(result.errors[0]?.message ?? "Assignment could not be created");
    }
    return assignment;
  });
}

/** List all sent forms for a specific patient. */
export function listSendsByPatient(
  patientId: string,
  params?: { page?: number; per_page?: number },
): Promise<PaginatedResponse<QuestionnaireSend>> {
  return listQuestionnaireAssignments({ ...params, patient_id: patientId });
}

/** List all completed form responses for a specific patient. */
export function listResponsesByPatient(
  patientId: string,
  params?: { page?: number; per_page?: number },
): Promise<PaginatedResponse<QuestionnaireResponseRecord>> {
  return apiGetList<QuestionnaireResponseRecord>(
    `/patients/${patientId}/questionnaire-responses`,
    { params },
  );
}

/** Get a specific questionnaire response by ID. */
export function getQuestionnaireResponse(
  responseId: string,
): Promise<QuestionnaireResponseRecord> {
  return apiGet<QuestionnaireResponseRecord>(
    `/questionnaire-responses/${responseId}`,
  );
}

export interface SubmitQuestionnaireResponseRequest {
  questionnaire_id: string;
  patient_id: string;
  encounter_id?: string;
  answers: string;
  status: "in-progress" | "completed" | "amended" | "entered-in-error" | "stopped";
  authored_at?: string;
}

/** Clinician-entered (or staff) questionnaire response - files to patient chart. */
export function submitQuestionnaireResponse(
  req: SubmitQuestionnaireResponseRequest,
): Promise<QuestionnaireResponseRecord> {
  return apiPost<QuestionnaireResponseRecord>(`/questionnaire-responses`, req);
}

export async function downloadSignedQuestionnaireArtifact(responseId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    `/questionnaire-responses/${responseId}/signed-artifact/download`,
    { responseType: "blob" },
  );
  return data;
}

export interface QuestionnaireSignatureInput {
  signer_name: string;
  signer_relationship: string;
  signer_authority: string;
  signature: string;
  initials?: string;
  acknowledgment: string;
  agreed: boolean;
}

export function cosignQuestionnaireResponse(
  responseId: string,
  role: "witness" | "provider",
  signature: QuestionnaireSignatureInput,
): Promise<QuestionnaireResponseRecord> {
  return apiPost<QuestionnaireResponseRecord>(
    `/questionnaire-responses/${responseId}/signatures`,
    { role, signature },
  );
}

export function amendQuestionnaireResponse(
  responseId: string,
  input: { answers: string; reason: string; signature: QuestionnaireSignatureInput },
): Promise<QuestionnaireResponseRecord> {
  return apiPost<QuestionnaireResponseRecord>(
    `/questionnaire-responses/${responseId}/amendments`,
    input,
  );
}

/** Cancel a sent form (marks status as cancelled, patient link becomes invalid). */
export function cancelQuestionnaireSend(sendId: string): Promise<void> {
  return cancelQuestionnaireAssignment(sendId).then(() => undefined);
}

/** Resend a form - cancels the old send and issues a fresh token to the patient. */
export function resendQuestionnaire(
  sendId: string,
  payload?: ResendQuestionnaireRequest,
): Promise<QuestionnaireSend> {
  return resendQuestionnaireAssignment(sendId, payload);
}
